import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  HostListener,
  inject,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { filter, Subject, switchMap, takeUntil } from 'rxjs';
import { AuthService } from '../../services/auth.service';
import { ButtonComponent } from '../../controls/button/button.component';
import { BreadcrumbsComponent } from '../breadcrumbs/breadcrumbs.component';
import { UserPermissionService } from '../../services/user-permission.service';
import { SidebarService } from '../../services/sidebar.service';
import { UserProfileImgComponent } from '../user-profile-img/user-profile-img.component';
import { ConfigService } from '../../services/config.service';
import { EnvTagComponent } from '../envTag/envTag.component';
import { shouldUseAuthenticatedLayout } from '../../utils/layout-mode.utils';
import { DealiodeskLogoComponent } from '../dealiodesk-logo/dealiodesk-logo.component';

interface NavItem {
  route: string;
  label: string;
  /** Material Symbols icon name */
  msoIcon: string;
  /** Visible when user has ANY of these permissions. Empty/undefined = always visible. */
  permissions?: string[];
}

@Component({
  selector: 'app-authenticated-wrapper',
  standalone: true,
  imports: [RouterOutlet, ButtonComponent, BreadcrumbsComponent, UserProfileImgComponent, EnvTagComponent, DealiodeskLogoComponent],
  templateUrl: './authenticated-wrapper.html',
  styleUrl: './authenticated-wrapper.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AuthenticatedWrapperComponent implements OnInit, OnDestroy {
  private _authService: AuthService = inject(AuthService);
  private _userPermissionService: UserPermissionService = inject(UserPermissionService);
  private _sidebarService: SidebarService = inject(SidebarService);
  private _router: Router = inject(Router);
  private _cdr: ChangeDetectorRef = inject(ChangeDetectorRef);
  private _destroy$: Subject<void> = new Subject<void>();
  private _config: ConfigService = inject(ConfigService);

  public currentRoute: string = '';
  public currentScreenWidth: number = window.innerWidth;
  public isCollapsedFromService: boolean = false;
  public isRefreshingPermissions: boolean = false;

  public readonly activeRouteStyles: string =
    'background: linear-gradient(90deg, rgba(6, 83, 171, 0.10) 86.04%, rgba(0, 67, 143, 0.04) 100%) !important;';

  private readonly _workflowChildRoutes: { path: string; permission: string }[] = [
    { path: 'workflows', permission: 'workflows.workflows.read' },
    { path: 'custom-fields', permission: 'workflows.customfields.read' },
    { path: 'field-layouts', permission: 'workflows.layouts.read' },
  ];

  public readonly navItems: NavItem[] = [
    // { route: '/dashboard', label: 'Dashboard', msoIcon: 'dashboard' },
    { route: '/underwrites', label: 'Underwrites', msoIcon: 'description', permissions: ['underwrites.read'] },
    { route: '/property-transactions', label: 'Transactions', msoIcon: 'currency_exchange', permissions: ['transactions.read'] },
    { route: '/workflows', label: 'Workflows', msoIcon: 'conversion_path', permissions: ['workflows.workflows.read', 'workflows.customfields.read', 'workflows.layouts.read'] },
    { route: '/contacts', label: 'Contacts', msoIcon: 'patient_list', permissions: ['contacts.contractors.read', 'contacts.capitalproviders.read'] },
    { route: '/settings', label: 'Settings', msoIcon: 'settings', permissions: ['settings.roles.read', 'settings.team.read', 'settings.departments.read'] },
    // { route: '/contact/contractor', label: 'Contractor', msoIcon: 'engineering', permissions: ['contacts.contractors.read'] },
    // { route: '/contact/capital-providers', label: 'Capital providers', msoIcon: 'diamond', permissions: ['contacts.capitalproviders.read'] },
    // { route: '/change-requests', label: 'Change Requests', msoIcon: 'edit_arrow_up', permissions: ['changerequests.read'] }, //temporary disabled
  ];

  @HostListener('window:resize')
  public onResize(): void {
    this.currentScreenWidth = window.innerWidth;
    if (this.currentScreenWidth <= 900) {
      this._sidebarService.collapseSidebar();
    }
    this._cdr.detectChanges();
  }

  public ngOnInit(): void {
    this.currentRoute = this._router.url;
    this._subscribeToRouteChanges();
    this._subscribeToSidebarState();

    if (this.currentScreenWidth <= 900) {
      this._sidebarService.collapseSidebar();
    }
  }

  public get isPreflightEnv(): boolean {
    const raw = this._config.apiUrl;
    if (!raw) {
      return false;
    }

    try {
      const { hostname } = new URL(raw);
      const h = hostname.toLowerCase();

      if (h.includes('localhost') || h === '127.0.0.1' || h.endsWith('.localhost')) {
        return true;
      }

      if (h.includes('dev') || h.startsWith('dev.') || h.startsWith('dev-')) {
        return true;
      }
      if (h.includes('staging') || h.startsWith('staging.') || h.startsWith('staging-')) {
        return true;
      }
      if (h.includes('uat') || h.startsWith('uat.') || h.startsWith('uat-')) {
        return true;
      }
      if (/(\.|^)(dev|staging)(\.|$)/.test(h)) {
        return true;
      }

      if (h.includes('-services-')) {
        return true;
      }

      return false;
    } catch {
      return false;
    }
  }

  public ngOnDestroy(): void {
    this._destroy$.next();
    this._destroy$.complete();
  }

  private _subscribeToRouteChanges(): void {
    this._router.events
      .pipe(
        filter((ev): ev is NavigationEnd => ev instanceof NavigationEnd),
        takeUntil(this._destroy$)
      )
      .subscribe((ev: NavigationEnd): void => {
        this.currentRoute = ev.urlAfterRedirects;
        this._cdr.detectChanges();
      });
  }

  private _subscribeToSidebarState(): void {
    this._sidebarService.isCollapsed$
      .pipe(takeUntil(this._destroy$))
      .subscribe((collapsed: boolean): void => {
        this.isCollapsedFromService = collapsed;
        this._cdr.detectChanges();
      });
  }

  public get shouldShowWrapper(): boolean {
    return shouldUseAuthenticatedLayout(this.currentRoute);
  }

  public get currentUserDisplayName(): string {
    const claims = this._authService.getClaims();
    if (claims?.fName || claims?.lName) {
      return `${claims.fName || ''} ${claims.lName || ''}`.trim();
    }
    return claims?.email || '';
  }

  public get currentUserFirstName(): string {
    return this._authService.getClaims()?.fName || '';
  }
  public get currentUserLastName(): string {
    return this._authService.getClaims()?.lName || '';
  }

  public get currentUserEmail(): string {
    return this._authService.getClaims()?.email || '';
  }

  public get currentUserRole(): string {
    return this._authService.getClaims()?.role || JSON.parse(localStorage.getItem('userPermissions') || '{}').roleName || 'User';
  }

  public get isCollapsed(): boolean {
    if (this.currentScreenWidth <= 900) {
      return true;
    }
    return this.isCollapsedFromService;
  }

  public get isBelowTabletBreakpoint(): boolean {
    return this.currentScreenWidth <= 900;
  }

  public isActiveRoute(route: string): boolean {
    return this.currentRoute.startsWith(route);
  }

  public getNavItemStyle(route: string): string {
    return this.isActiveRoute(route) ? this.activeRouteStyles : '';
  }

  public navigateTo(route: string): void {
    if (route === '/workflows') {
      const firstAllowed = this._workflowChildRoutes.find(
        r => this._userPermissionService.hasPermission(r.permission)
      );
      if (firstAllowed) {
        this._router.navigateByUrl(`/workflows/${firstAllowed.path}`);
        return;
      }
    }
    this._router.navigateByUrl(route);
  }

  public navigateToHome(): void {
    this._router.navigateByUrl('/dashboard');
  }

  public toggleSidebar(): void {
    this._sidebarService.toggleSidebar();
  }

  public handleLogout(): void {
    this._authService.logout();
  }

  public hasPermission(permissions: string[] | undefined): boolean {
    if (!permissions || permissions.length === 0) return true;
    return this._userPermissionService.hasAnyPermission(permissions);
  }

  // TODO: temporary – remove after testing
  public refreshPermissions(): void {
    this.isRefreshingPermissions = true;
    this._cdr.markForCheck();

    this._authService.refreshClaims()
      .pipe(switchMap(() => this._userPermissionService.loadCurrentUserPermissions()))
      .subscribe({
        next: () => {
          this.isRefreshingPermissions = false;
          this._cdr.markForCheck();
          window.location.reload();
        },
        error: () => {
          this.isRefreshingPermissions = false;
          this._cdr.markForCheck();
        },
      });
  }

  public navigateToDocuments(): void {
    this._router.navigate(['/documents']);
  }

  public navigateToDashboard(): void {
    this._router.navigate(['/dashboard']);
  }
}
